import { TextField } from '@components/form/TextField/TextField';
import { styled } from '@mui/material';
import { format } from 'date-fns';
import { utcToZonedTime, zonedTimeToUtc } from 'date-fns-tz';
import React, { ComponentProps, memo } from 'react';
import { FormattedMessage } from 'react-intl';

import { useCurrentUserClientTimezone } from '../../../contexts/OidcContext/OidcContext.selectors';

const StyledTimeField = styled(TextField)`
    input::-webkit-calendar-picker-indicator {
        filter: invert(100%);
    }
`;

export const TimeField = memo(function TimeField({
    label,
    name,
    date,
    value,
    onChange,
    onBlur,
    step,
    inputReadOnly,
    ...rest
}: {
    label?: string | React.ReactNode;
    name: string;
    date: number | Date; // Day
    value?: number | Date | null; // hour + minutes
    onChange?: (value: number | Date | null) => void;
    onBlur?: React.FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>;
    step?: number;
    inputReadOnly?: boolean;
} & ComponentProps<typeof TextField>) {
    const timezone = useCurrentUserClientTimezone();

    return (
        <StyledTimeField
            label={
                <>
                    {label} <FormattedMessage id={timezone === 'UTC' ? 'form.utcTime' : 'form.localTime'} />
                </>
            }
            id={name}
            name={name}
            type="time"
            onBlur={onBlur}
            {...rest}
            defaultValue={value ? format(utcToZonedTime(value, timezone || ''), 'HH:mm') : null}
            inputProps={{
                step: step || 5 * 60, // in seconds, 5 min increments
                readOnly: inputReadOnly,
            }}
            onChange={e => {
                if (!e.target.value) {
                    onChange?.(null);
                } else {
                    const updatedTime = new Date(date).setHours(
                        parseInt(e.target.value.substring(0, 2)),
                        parseInt(e.target.value.substring(3, 5)),
                    );

                    onChange?.(zonedTimeToUtc(updatedTime, timezone || '').getTime());
                }
            }}
        />
    );
});
